import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { useTranslation, Trans } from "react-i18next";
import BusinessCustomersActions from "../../../Redux/transition/customers/business-customer.reducer";
import CustomToast from '../../../Components/CustomToast';
import "../../../Styles/components/tags.scss";
import { buildGroupedTagOptions } from "../../../utils/tagGroupOptions";
import { TAG_GROUP_DROPDOWN_PAGE_OPTIONS } from "../Segments/tagUtils";

const resolveTagId = (selected) => {
    const item = Array.isArray(selected) ? selected[0] : selected;
    if (item == null) return null;
    return typeof item === 'object' ? (item.value ?? item.id) : item;
};

export default function AddSubscriberToTagDialog({ refreshCustomers, visible, onHide, selectedCustomers, setSelectedCustomers, rowSubClicked }) {

    const { t } = useTranslation("common");
    const [associateToastTrigger, setAssociateToastTrigger] = useState(false);
    const [clickAdd, setClickAdd] = useState(false);
    const [associateToast, setAssociateToast] = useState({
        severity: "",
        detail: "",
        summary: "",
        life: 0
    });
    const [bulkAssociateToastTrigger, setBulkAssociateToastTrigger] = useState(false);
    const [clickBulkAdd, setClickBulkAdd] = useState(false);
    const [bulkAssociateToast, setBulkAssociateToast] = useState({
        severity: "",
        detail: "",
        summary: "",
        life: 0
    });
    const [selectedTags, setSelectedTags] = useState(null);
    const [existingCustomerError, setExistingCustomerError] = useState(false)

    //Dispatch Actions
    const dispatch = useDispatch();
    const getTags = useCallback((businessId) => dispatch(BusinessCustomersActions.tagsRequest(businessId)), [dispatch]);
    const getTagGroups = useCallback(
        (id) =>
            dispatch(
                BusinessCustomersActions.tagGroupRequest(id, TAG_GROUP_DROPDOWN_PAGE_OPTIONS)
            ),
        [dispatch]
    );
    const getTagGroupsGraphed = useCallback((businessId) => dispatch(BusinessCustomersActions.tagGroupGraphedRequest(businessId)), [dispatch]);

    const associateSubscriberToTag = useCallback((tagId, bcId) => { dispatch(BusinessCustomersActions.associateSubscriberToTagRequest(tagId, bcId)) }, [dispatch]);
    const bulkAssociateSubscriberFromTag = useCallback((tagId, bcIds) => { dispatch(BusinessCustomersActions.bulkAssociateSubscriberToTagRequest(tagId, bcIds)) }, [dispatch]);

    //Redux State from Store
    const defMembership = useSelector((state) => state.defMembership.defMembership);
    const { associatingTag, associatedTag, associatingTagError, bulkAssociatingTag, bulkAssociatedTag, tagGroupsGraphed, tagGroups, tags } = useSelector((state) => state.businessCustomers);

    const addTagErrorMessage = associatingTagError?.detail?.split('"')?.[1] ?? associatingTagError?.detail;

    useEffect(() => {
        if (!visible || !defMembership?.business?.id) {
            return;
        }
        const businessId = defMembership.business.id;
        getTagGroupsGraphed(businessId);
        getTagGroups(businessId);
        getTags(businessId);
    }, [visible, defMembership?.business?.id, getTagGroupsGraphed, getTagGroups, getTags]);

    useEffect(() => {
        if (!associatingTag && clickAdd && associatedTag) {
            showAssociateToast("success", t("tags.success"), t("tags.tag_success_summary"))
        }
        if (!associatingTag && clickAdd && associatingTagError) {
            showAssociateToast("error", t("tags.error"), addTagErrorMessage)
        }
        if (!bulkAssociatingTag && clickBulkAdd && bulkAssociatedTag) {
            showBulkAssociateToast("success", t("tags.success"), t("tags.bulk_success_summary"))
        }
        if (!bulkAssociatingTag && clickBulkAdd && !bulkAssociatedTag) {
            showBulkAssociateToast("error", t("tags.error"), t("tags.bulk_error_summary"))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [associatingTag, clickAdd, associatingTagError, associatedTag, bulkAssociatingTag, clickBulkAdd, bulkAssociatedTag]);

    const showAssociateToast = (severity, summary, detail) => {
        setAssociateToast({
            severity,
            summary,
            detail,
            life: 4000,
        });
        setAssociateToastTrigger(true);
    }

    const showBulkAssociateToast = (severity, summary, detail) => {
        setBulkAssociateToast({
            severity,
            summary,
            detail,
            life: 4000,
        });
        setBulkAssociateToastTrigger(true);
    }

    const handleAssociate = () => {
        const tagId = resolveTagId(selectedTags);
        if (!tagId) {
            return;
        }
        if (rowSubClicked) {
            const singleCustomerId = rowSubClicked.businessCustomer.id;
            setClickAdd(true);
            associateSubscriberToTag(tagId, singleCustomerId);
        } else {
            const multipleCustomerId = selectedCustomers.map((item) => item.businessCustomer?.id ?? item.id);
            setClickBulkAdd(true);
            bulkAssociateSubscriberFromTag(tagId, multipleCustomerId);
        }
    }

    const handleSelectionChange = (e) => {
        const incoming = Array.isArray(e?.value) ? e.value : [];
        const lastSelected = incoming.length > 0 ? [incoming[incoming.length - 1]] : [];
        setSelectedTags(lastSelected);
    };

    const groupedOptions = useMemo(() => {
        const assignedTagIds = new Set((rowSubClicked?.tags || []).map((tag) => tag?.id));

        return buildGroupedTagOptions(tagGroupsGraphed, tagGroups, tags)
            .map((group) => ({
                ...group,
                items: group.items.filter((item) => !assignedTagIds.has(item.value)),
            }))
            .filter((group) => group.items.length > 0);
    }, [tagGroupsGraphed, tagGroups, tags, rowSubClicked]);

    return (
        <Dialog
            header={t("tags.add_dialog_header")}
            visible={visible}
            style={{ width: '350px' }}
            onHide={() => {
                setSelectedTags(null);
                onHide();
                setExistingCustomerError(false);
            }}
        >
            <CustomToast
                {...associateToast}
                trigger={associateToastTrigger}
                handleActionsAfterToast={() => {
                    setAssociateToastTrigger(false);
                    setClickAdd(false);
                    onHide()
                    refreshCustomers();
                    getTagGroupsGraphed(defMembership.business.id);
                    getTagGroups(defMembership.business.id);
                    getTags(defMembership.business.id);
                    setSelectedCustomers([]);
                    setSelectedTags(null);
                    setExistingCustomerError(false);
                }}
                handleActionsAfterErrorToast={() => {
                    setAssociateToastTrigger(false);
                    setClickAdd(false);
                    setSelectedTags(null);
                    setExistingCustomerError(true);
                    refreshCustomers();
                }}
            />
            <CustomToast
                {...bulkAssociateToast}
                trigger={bulkAssociateToastTrigger}
                handleActionsAfterToast={() => {
                    setBulkAssociateToastTrigger(false);
                    setClickBulkAdd(false);
                    onHide()
                    refreshCustomers();
                    getTags(defMembership.business.id);
                    setSelectedCustomers([]);
                    setSelectedTags(null);
                }}
                handleActionsAfterErrorToast={() => {
                    setBulkAssociateToastTrigger(false);
                    onHide();
                    setClickBulkAdd(false);
                    setSelectedTags(null);

                }}
            />

            <div className="p-fluid add-main-div">
                <div>
                    <Trans
                        i18nKey={"tags.add_tag_dialog_hint"}
                        components={{
                            1: <span className='bold_red_hint' />,
                            2: <span className='bold_black_hint' />,
                            3: <span className='normal_hint' />
                        }}
                    />
                </div>

                <hr className='tag_hr' />

                <div className='mt-3'>
                    <div className='mb-1'><label htmlFor="tagLabel" className='tag_label'>{t("tags.choose_tag")}</label></div>

                    <MultiSelect
                        className="tag_select custom-multiselect"
                        panelClassName="tag-multiselect-panel"
                        id="tag_select_id"
                        value={selectedTags}
                        onChange={(e) => {
                            handleSelectionChange(e);
                            setExistingCustomerError(false);
                        }}
                        options={groupedOptions}
                        optionLabel="label"
                        optionGroupLabel="label"
                        optionGroupChildren="items"
                        placeholder={t("tags.select_tag")}
                        showSelectAll={false}
                        display="chip"
                    />
                    <div>
                        {existingCustomerError ? <p className='tags_error_message mt-1'>{t("tags.existing_customer_error")}</p> : null}
                    </div>
                </div>

                <div className='flex justify-content-end mt-4 gap-3 align-items-center'>
                    <Button label={t('subscriber.cancel')}
                        icon="pi pi-times"
                        onClick={() => {
                            setSelectedTags(null);
                            onHide();
                            setExistingCustomerError(false);
                        }}
                        className="p-button-text"
                        id="tag_cancel_btns"
                    />
                    <Button
                        id='tag_add_btn'
                        label={t("tags.add_btn_text")}
                        onClick={() => {
                            handleAssociate()
                        }}
                        disabled={associatingTag || clickAdd || bulkAssociatingTag || clickBulkAdd || !selectedTags}
                        loading={associatingTag || bulkAssociatingTag}
                    />
                </div>
            </div>
        </Dialog >
    );
}